import { useState } from 'react'

import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18n-lite'

import { videoTypes } from '../../../shared/WebcamStreamCapture/components/Player'

import api from 'utils/api'
import { httpUpload, HTTPUploadProps } from './httpUpload'

export type UploadFileProps = {
  file: string | File
  fileType?: string
}

export type UploadFileResult = {
  url: string
  content: File
}

type UploadRequestParams = {
  filename: string
  contentType: string
  body: File
}

const useUploadS3 = () => {
  const { t } = useTranslation()

  // ainda não é permitido a escolha do tipo de vídeo, mas já estou abrindo o caminho
  const [videoType] = useState(videoTypes[0])

  const [progress, setProgress] = useState(0)
  const [currentStatus, setCurrentStatus] = useState(
    t('activity-input-upload-s3.status.awaiting-upload')
  )

  const uploadFile = async ({
    file,
    fileType
  }: UploadFileProps): Promise<UploadFileResult> => {
    setCurrentStatus(t('activity-input-upload-s3.status.uploading-file'))

    const handleError = (error: unknown) => {
      setCurrentStatus(
        t('activity-input-upload-s3.status.upload-error') +
          (error instanceof Error ? ` ${error.message}` : '')
      )

      throw error
    }

    try {
      let params: UploadRequestParams | null = null

      if (file instanceof File) {
        params = generateDefaultUploadParams(file)
      } else if (fileType === 'base64') {
        params = await generateBase64ImageUploadParams(file)
      } else if (fileType === 'blob') {
        params = await generateBlobVideoUploadParams(file, videoType)
      }

      if (!params) {
        throw new TypeError('Invalid parameters')
      }

      const presignedUploadResponse = await api.postSignedUploadUrl(
        {
          content_type: params.contentType,
          filename: params.filename
        },
        true
      )

      const uploadRequestParams: HTTPUploadProps = {
        url: presignedUploadResponse.signed_url,
        body: params.body,
        contentType: params.contentType,
        onProgress: setProgress
      }

      await httpUpload(uploadRequestParams)

      setCurrentStatus(t('activity-input-upload-s3.status.upload-success'))

      return {
        url: presignedUploadResponse.signed_url,
        content: new File([params.body], presignedUploadResponse.file_key, {
          type: params.contentType,
          lastModified: params.body.lastModified
        })
      }
    } catch (e) {
      return handleError(e)
    }
  }

  return {
    progress,
    currentStatus,
    uploadFile
  }
}

const generateBase64ImageUploadParams = async (
  dataUri: string
): Promise<UploadRequestParams> => {
  const [, imageType, base64Content] =
    dataUri.match(/^data:image\/(\w+);base64,(.+)$/) || []

  if (!imageType) {
    throw new Error('Invalid image data URI')
  }

  const file = new File(
    [Buffer.from(base64Content, 'base64') as BlobPart],
    `image.${imageType}`,
    {
      type: `image/${imageType}`
    }
  )

  return {
    filename: file.name,
    body: file,
    contentType: file.type
  }
}

const generateBlobVideoUploadParams = async (
  file: string,
  videoType: string
): Promise<UploadRequestParams> => {
  const blob = await fetch(file).then((r) => r.blob())
  const filename = `${uuidv4()}.${videoType.replace('video/', '')}`
  return {
    filename,
    body: new global.File([blob], filename, {
      type: videoType,
      lastModified: Date.now()
    }),
    contentType: videoType
  }
}

const generateDefaultUploadParams = (file: File): UploadRequestParams => {
  return {
    filename: file.name,
    body: file,
    contentType: file.type
  }
}

export default useUploadS3
